// Translation fields per language
export interface TranslationFields {
  [key: string]: string | undefined;
}

export interface Translations {
  fr?: TranslationFields;
  es?: TranslationFields;
}

// Salon schedule settings
export interface DaySchedule {
  isOpen: boolean;
  openTime: string;  // "09:00"
  closeTime: string; // "18:00"
}

export interface SalonSettings {
  businessHours: {
    [day: number]: DaySchedule; // 0=Sun, 1=Mon, ..., 6=Sat
  };
  holidays: string[]; // YYYY-MM-DD dates where salon is closed
}

export const DEFAULT_SALON_SETTINGS: SalonSettings = {
  businessHours: {
    0: { isOpen: false, openTime: '09:00', closeTime: '18:00' }, // Sunday
    1: { isOpen: true,  openTime: '09:00', closeTime: '18:00' }, // Monday
    2: { isOpen: true,  openTime: '09:00', closeTime: '18:00' }, // Tuesday
    3: { isOpen: true,  openTime: '09:00', closeTime: '18:00' }, // Wednesday
    4: { isOpen: true,  openTime: '09:00', closeTime: '18:00' }, // Thursday
    5: { isOpen: true,  openTime: '09:00', closeTime: '18:00' }, // Friday
    6: { isOpen: true,  openTime: '09:00', closeTime: '17:00' }, // Saturday
  },
  holidays: [],
};

// Service category
export interface Category {
  id: string;
  name: string;
  translations?: {
    fr?: { name?: string };
    es?: { name?: string };
  };
}

// Service types
export interface Service {
  id: string;
  name: string;
  description: string;
  duration: number; // in minutes
  price: {
    min: number;
    max: number;
  };
  imageUrl: string;
  category: string;
  popular?: boolean;
  showPrice?: boolean;
  translations?: {
    fr?: { name?: string; description?: string };
    es?: { name?: string; description?: string };
  };
}

// Appointment types
export interface Appointment {
  id: string;
  clientId?: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  serviceId: string;
  teamMemberId?: string;
  date: string; // ISO string
  startTime: string; // 24h format: "14:30"
  endTime?: string; // 24h format: "16:30"
  notes?: string;
  status: 'confirmed' | 'completed' | 'cancelled' | 'no-show' | 'archived';
  emailNotifications?: boolean;
  smsNotifications?: boolean;
  reminderSent?: boolean;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}

// User types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: 'client' | 'admin';
  createdAt: string; // ISO string
}

// Client specific data
export interface Client extends User {
  role: 'client';
  appointments: string[]; // Array of appointment IDs
}

// Admin specific data
export interface Admin extends User {
  role: 'admin';
}

// Time slot type
export interface TimeSlot {
  date: string; // ISO string
  startTime: string; // 24h format: "14:30"
  endTime: string; // 24h format: "16:30"
  available: boolean;
}

// Blocked time periods
export interface BlockedTime {
  id: string;
  date: string; // Date string in YYYY-MM-DD format
  startTime: string; // Time string in HH:mm format
  endTime: string; // Time string in HH:mm format
  reason?: string;
  teamMemberId?: string; // If set, only blocks that team member's availability
  translations?: {
    fr?: { reason?: string };
    es?: { reason?: string };
  };
}

// Team member types
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  specialties: string[];
  bio?: string;
  imageUrl?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  workingDays: number[]; // 0=Sun, 1=Mon, ..., 6=Sat
  workingHours?: { [day: number]: { openTime: string; closeTime: string } }; // per-day hours override
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  translations?: {
    fr?: { role?: string; bio?: string };
    es?: { role?: string; bio?: string };
  };
}

// Testimonial types
export interface Testimonial {
  id: string;
  clientName: string;
  content: string;
  rating: number; // 1-5 stars
  service?: string;
  location?: string;
  imageUrl?: string;
  isActive: boolean;
  featured?: boolean;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  translations?: {
    fr?: { content?: string };
    es?: { content?: string };
  };
}

// Notification types
export interface Notification {
  id: string;
  userId: string;
  type: 'confirmation' | 'reminder' | 'cancellation' | 'modification';
  message: string;
  read: boolean;
  createdAt: string; // ISO string
}

// Product types
export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  quantity: number; // Stock disponible
  status: 'active' | 'inactive';
  images: string[]; // URLs des images (max 3)
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}

// Cart item types
export interface CartItem {
  productId: string;
  product: Product;
  quantity: number;
}

// Order types
export interface Order {
  id: string;
  clientInfo: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'completed' | 'cancelled';
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}

export interface OrderItem {
  productId: string;
  productName: string;
  productPrice: number;
  quantity: number;
  subtotal: number;
}

// Special request — client wants a style not listed
export interface SpecialRequest {
  id: string;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  description: string;            // what they want
  preferredDate?: string;         // YYYY-MM-DD
  preferredTime?: string;         // HH:mm
  preferredStylistId?: string;    // optional team member suggestion
  photoUrl?: string;              // reference photo uploaded by client
  status: 'new' | 'reviewed' | 'contacted' | 'declined';
  adminNotes?: string;
  createdAt: string;
}
